import React from 'react';
import { OccupancySlot } from '../../types/venueBooking';
import { sectionLabel } from '../../utils/venueSections';
import { hourRange, layoutLanes, sameDay, slotStyle, timeOf } from './occupancy';

interface DailyOccupancyGridProps {
  slots: OccupancySlot[];
  date: Date;
  venueSections: number;
  /** A részek nevei (név híján "N. rész") */
  sectionNames?: string[];
  /** Foglalhat-e: ekkor az üres cellára kattintva foglalás indul. */
  canBook: boolean;
  onEmptyCellClick: (startTime: Date, section: number | null) => void;
  onSlotClick: (slot: OccupancySlot) => void;
}

const HOUR_HEIGHT = 52; // px / óra

/**
 * Egy nap idővonala pályarészenként (asztali nézet): oszlop részenként, a
 * foglalások a helyükön. Csak a lényeges órák látszanak, a mai napon egy
 * vonal jelzi a mostani időt.
 */
const DailyOccupancyGrid: React.FC<DailyOccupancyGridProps> = ({
  slots,
  date,
  venueSections,
  sectionNames,
  canBook,
  onEmptyCellClick,
  onSlotClick,
}) => {
  const active = slots.filter((s) => !s.isCancelled);
  const [startHour, endHour] = hourRange(active);
  const hours = Array.from({ length: endHour - startHour }, (_, i) => startHour + i);
  const sections: (number | null)[] = venueSections > 1 ? Array.from({ length: venueSections }, (_, i) => i + 1) : [null];

  const minutesFromTop = (value: Date) => (value.getHours() - startHour) * 60 + value.getMinutes();
  const position = (slot: OccupancySlot): React.CSSProperties => {
    const s = new Date(slot.startTime);
    const e = new Date(slot.endTime);
    const top = Math.max(0, (minutesFromTop(s) / 60) * HOUR_HEIGHT);
    const bottom = Math.min(hours.length * HOUR_HEIGHT, (minutesFromTop(sameDay(e, date) ? e : new Date(new Date(date).setHours(endHour, 0, 0, 0))) / 60) * HOUR_HEIGHT);
    return { top, height: Math.max(22, bottom - top - 2) };
  };

  const forSection = (section: number | null) =>
    active.filter((s) => s.section === null || section === null || s.section === section);

  const now = new Date();
  const nowTop = sameDay(now, date) ? (minutesFromTop(now) / 60) * HOUR_HEIGHT : null;

  const handleColumnClick = (e: React.MouseEvent<HTMLDivElement>, section: number | null) => {
    if (!canBook) return;
    const y = e.clientY - e.currentTarget.getBoundingClientRect().top;
    const halfHours = Math.floor(y / (HOUR_HEIGHT / 2));
    const start = new Date(date);
    start.setHours(startHour + Math.floor(halfHours / 2), (halfHours % 2) * 30, 0, 0);
    onEmptyCellClick(start, section);
  };

  const cols = `3.5rem repeat(${sections.length}, minmax(0, 1fr))`;

  return (
    <div>
      <div className="grid border-b border-gray-200 bg-gray-50" style={{ gridTemplateColumns: cols }}>
        <div />
        {sections.map((section, i) => (
          <div key={i} className="truncate border-l border-gray-200 px-2 py-2.5 text-center text-xs font-semibold text-gray-700">
            {section === null ? 'Teljes pálya' : sectionLabel(section, sectionNames)}
          </div>
        ))}
      </div>

      <div className="grid" style={{ gridTemplateColumns: cols }}>
        <div className="relative">
          {hours.map((h) => (
            <div key={h} className="relative text-right" style={{ height: HOUR_HEIGHT }}>
              <span className="absolute -top-2 right-2 bg-white px-0.5 text-[11px] tabular-nums text-gray-400">
                {h !== startHour && `${String(h).padStart(2, '0')}:00`}
              </span>
            </div>
          ))}
        </div>

        {sections.map((section, i) => (
          <div
            key={i}
            className={`relative border-l border-gray-200 ${canBook ? 'cursor-pointer' : ''}`}
            style={{ height: hours.length * HOUR_HEIGHT }}
            onClick={(e) => handleColumnClick(e, section)}
            title={canBook ? 'Kattints egy szabad időpontra a foglaláshoz' : undefined}
          >
            {hours.map((h) => (
              <div key={h} className="border-b border-gray-100 transition-colors hover:bg-primary-50/40" style={{ height: HOUR_HEIGHT }}>
                <div className="h-1/2 border-b border-dashed border-gray-50" />
              </div>
            ))}

            {nowTop !== null && nowTop >= 0 && nowTop <= hours.length * HOUR_HEIGHT && (
              <div className="pointer-events-none absolute inset-x-0 z-10 border-t-2 border-red-500" style={{ top: nowTop }}>
                {i === 0 && <span className="absolute -left-1 -top-[5px] h-2 w-2 rounded-full bg-red-500" />}
              </div>
            )}

            {(() => {
              const sectionSlots = forSection(section);
              const lanes = layoutLanes(
                sectionSlots,
                (s) => new Date(s.startTime).getTime(),
                (s) => new Date(s.endTime).getTime()
              );
              return sectionSlots.map((slot) => {
              const style = slotStyle(slot.type);
              const pos = position(slot);
              const { lane, lanes: laneCount } = lanes.get(slot) ?? { lane: 0, lanes: 1 };
              return (
                <button
                  key={`${slot.source}-${slot.id}-${slot.section ?? 'all'}`}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSlotClick(slot);
                  }}
                  className={`absolute z-[5] flex overflow-hidden rounded-lg text-left shadow-sm ring-1 transition hover:shadow-md ${style.block}`}
                  style={{
                    ...pos,
                    left: `calc(${(lane / laneCount) * 100}% + 4px)`,
                    width: `calc(${100 / laneCount}% - 8px)`,
                  }}
                  title={`${slot.title} (${timeOf(slot.startTime)}–${timeOf(slot.endTime)})`}
                >
                  <span className={`w-1 shrink-0 ${style.bar}`} />
                  <span className="min-w-0 px-2 py-1">
                    <span className="block truncate text-xs font-semibold">{slot.title}</span>
                    {(pos.height as number) > 34 && (
                      <span className="block truncate text-[11px] opacity-75">
                        {timeOf(slot.startTime)}–{timeOf(slot.endTime)}
                        {slot.ageGroup && ` · ${slot.ageGroup}`}
                      </span>
                    )}
                  </span>
                </button>
              );
              });
            })()}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DailyOccupancyGrid;
